import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Gift, X, PartyPopper, Heart, MessageCircle } from 'lucide-react';

const CONFETTI_COLORS = ['#FF6B6B', '#FFD93D', '#6BCB77', '#4D96FF', '#FF6FCF', '#A66CFF', '#FF9F45'];

const ConfettiPiece = ({ style }) => (
  <div className="absolute pointer-events-none" style={style}>
    <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: style.color, transform: `rotate(${style.rotation}deg)` }} />
  </div>
);

const BirthdaySplash = ({ onClose }) => {
  const [birthdays, setBirthdays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confetti, setConfetti] = useState([]);
  const animRef = useRef(null);

  const { getAuthHeaders, currentOffice } = useAuth();
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchBirthdays();
  }, []);

  useEffect(() => {
    if (birthdays.length > 0) {
      launchConfetti();
    }
  }, [birthdays]);

  const storageKey = `birthday_dismissed_${new Date().toISOString().split('T')[0]}`;

  const fetchBirthdays = async () => {
    // If already dismissed or wished today, don't show
    if (localStorage.getItem(storageKey)) {
      onClose();
      return;
    }
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;
      const res = await fetch(`${API_URL}/api/employees/birthdays/today`, { headers });
      if (res.ok) {
        const data = await res.json();
        setBirthdays(data);
        if (data.length === 0) onClose();
      } else {
        onClose();
      }
    } catch (err) {
      console.error(err);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const dismiss = () => {
    localStorage.setItem(storageKey, 'true');
    onClose();
  };

  const launchConfetti = () => {
    const pieces = [];
    for (let i = 0; i < 80; i++) {
      pieces.push({
        id: i,
        left: `${Math.random() * 100}%`,
        color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
        rotation: Math.random() * 360,
        animationDelay: `${Math.random() * 2}s`,
        animationDuration: `${2 + Math.random() * 3}s`,
      });
    }
    setConfetti(pieces);
  };

  const handleWish = (emp) => {
    localStorage.setItem(storageKey, 'true');
    onClose();
    navigate('/ping', { state: { openChatWith: { id: emp.user_id, first_name: emp.first_name, last_name: emp.last_name } } });
  };

  if (loading || birthdays.length === 0) return null;

  const isSingle = birthdays.length === 1;
  const firstName = isSingle ? birthdays[0].first_name : null;
  const fullName = isSingle ? `${birthdays[0].first_name} ${birthdays[0].last_name}` : null;

  return (
    <div className="fixed inset-0 z-[9997] flex items-center justify-center" data-testid="birthday-splash">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={dismiss} />

      {/* Confetti */}
      <style>{`
        @keyframes confettiFall {
          0% { transform: translateY(-100vh) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
        }
        .confetti-piece {
          animation: confettiFall var(--duration) ease-in-out var(--delay) forwards;
        }
        @keyframes pulseGlow {
          0%, 100% { box-shadow: 0 0 20px rgba(255,107,107,0.3); }
          50% { box-shadow: 0 0 40px rgba(255,107,107,0.6); }
        }
        @keyframes slideUp {
          0% { transform: translateY(30px); opacity: 0; }
          100% { transform: translateY(0); opacity: 1; }
        }
        .birthday-card {
          animation: slideUp 0.5s ease-out forwards, pulseGlow 2s ease-in-out infinite;
        }
      `}</style>

      {confetti.map(p => (
        <div key={p.id} className="confetti-piece absolute pointer-events-none w-3 h-3 rounded-sm"
          style={{
            left: p.left,
            top: '-20px',
            backgroundColor: p.color,
            '--delay': p.animationDelay,
            '--duration': p.animationDuration,
            transform: `rotate(${p.rotation}deg)`,
          }} />
      ))}

      {/* Card */}
      <div className="birthday-card relative bg-white rounded-3xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden" data-testid="birthday-card">
        {/* Top gradient */}
        <div className="h-2 bg-gradient-to-r from-pink-400 via-yellow-400 to-purple-400" />

        <button onClick={dismiss} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10" data-testid="birthday-close">
          <X className="h-5 w-5" />
        </button>

        <div className="px-8 py-8 text-center">
          {/* Cake icon */}
          <div className="w-20 h-20 mx-auto mb-5 rounded-full bg-gradient-to-br from-pink-100 to-yellow-100 flex items-center justify-center">
            <PartyPopper className="h-10 w-10 text-pink-500" />
          </div>

          {isSingle ? (
            <>
              {/* Single birthday */}
              {birthdays[0].profile_picture ? (
                <img src={birthdays[0].profile_picture} alt={fullName}
                  className="w-20 h-20 rounded-full mx-auto mb-4 object-cover ring-4 ring-pink-200" />
              ) : (
                <div className="w-20 h-20 rounded-full mx-auto mb-4 bg-gradient-to-br from-pink-400 to-purple-500 flex items-center justify-center text-white text-2xl font-bold ring-4 ring-pink-200">
                  {birthdays[0].first_name?.charAt(0)}
                </div>
              )}
              <h2 className="text-2xl font-bold text-gray-900 mb-1">
                It's {firstName.toUpperCase()}'s Birthday!
              </h2>
              <p className="text-sm text-gray-500 mb-4">
                {birthdays[0].job_title || ''} {birthdays[0].department ? `- ${birthdays[0].department}` : ''}
              </p>
            </>
          ) : (
            <>
              {/* Multiple birthdays */}
              <h2 className="text-2xl font-bold text-gray-900 mb-3">
                Birthday Celebrations Today!
              </h2>
              <div className="flex justify-center gap-4 mb-4 flex-wrap">
                {birthdays.map(emp => (
                  <div key={emp.id} className="flex flex-col items-center">
                    {emp.profile_picture ? (
                      <img src={emp.profile_picture} alt={emp.first_name}
                        className="w-14 h-14 rounded-full object-cover ring-3 ring-pink-200 mb-1" />
                    ) : (
                      <div className="w-14 h-14 rounded-full bg-gradient-to-br from-pink-400 to-purple-500 flex items-center justify-center text-white text-lg font-bold ring-3 ring-pink-200 mb-1">
                        {emp.first_name?.charAt(0)}
                      </div>
                    )}
                    <span className="text-xs font-medium text-gray-700">{emp.first_name}</span>
                  </div>
                ))}
              </div>
            </>
          )}

          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-6 text-left">
            <p className="text-sm text-gray-700 leading-relaxed flex items-start gap-2">
              <Heart className="h-4 w-4 text-pink-500 shrink-0 mt-0.5" />
              <span>
                Taking a moment to wish your colleague a happy birthday strengthens team bonds and makes
                the workplace more human. A simple message goes a long way in showing you care.
              </span>
            </p>
          </div>

          <div className="flex gap-3 justify-center flex-wrap">
            <Button variant="outline" onClick={dismiss} data-testid="birthday-later">
              Maybe Later
            </Button>
            {isSingle ? (
              <Button
                className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white"
                onClick={() => handleWish(birthdays[0])}
                data-testid="birthday-wish-btn"
              >
                <MessageCircle className="h-4 w-4 mr-2" />
                Wish {firstName}
              </Button>
            ) : (
              birthdays.map(emp => (
                <Button key={emp.id}
                  className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white"
                  onClick={() => handleWish(emp)}
                  data-testid={`birthday-wish-${emp.id}`}
                >
                  <MessageCircle className="h-4 w-4 mr-2" />
                  Wish {emp.first_name}
                </Button>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BirthdaySplash;
